import { graphql, PageProps } from "gatsby";
import {
  ECommerceHelper,
  LanguageHelper,
  extract,
  SiteInfoHelper,
  SearchHelper,
  SimplifyGatsbyQuery,
  getCurrentContentPageSlugsData,
  PageProviders,
  useMetadataZoom,
} from "@parksandresorts/core";
import ThemeHelper from "components/helpers/theme-helper.js";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import Page from "components/page";

export { Head } from "components/head";

type LunrStoreItem = {
  id: string;
  title: string;
  slug: string;
};

export type ContentfulContentPageProps = PageProps<
  SimplifyGatsbyQuery<Queries.ContentPageByQuery> & {
    allContentfulContentPage: { nodes: Array<{ node_locale: "en" | "sv" | "nn"; slug: string }> };
    contentfulContentPage: { node_locale: "en" | "sv" | "nn" };
  },
  {
    //What is being passed in as context in createPage in create-content-page.js
    slug: string;
    contentful_id: string;
    node_locale: string;
    excludePageFromExternalSearch: boolean | null;
    excludePageFromInternalSearch: boolean | null;
    pageTheme: string | null;
  }
>;
export default function ContentPageTemplate(props: ContentfulContentPageProps) {
  LanguageHelper.setLocale(props.data.contentfulContentPage.node_locale);
  SiteConfigurationStore.init(
    extract(props, "data.contentfulSiteConfiguration"),
    props.data.contentfulContentPage.node_locale,
  );
  ECommerceHelper.initJetshop(extract(props, "data.contentfulEcommerceConfig"));
  ThemeHelper.setTheme(extract(props, "data.contentfulSiteConfiguration.siteTheme", "sommarland"));

  SiteInfoHelper.setCalendarLoaderIcons({
    firstImage: "/loader-images/sun.svg",
    secondImage: "/loader-images/wave.svg",
    thirdImage: "/loader-images/glass.svg",
  });

  SearchHelper.getSingelton<LunrStoreItem>().setUrls({
    sv: {
      publicIndexURL: props.data.allLocalSearchSv?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchSv?.nodes[0]?.publicStoreURL,
    },
    en: {
      publicIndexURL: props.data.allLocalSearchEn?.nodes[0]?.publicIndexURL,
      publicStoreURL: props.data.allLocalSearchEn?.nodes[0]?.publicStoreURL,
    },
  });

  useMetadataZoom();

  return (
    <PageProviders
      currentContentPageData={{
        slugs: getCurrentContentPageSlugsData(props.data.allContentfulContentPage.nodes),
        blocks: props.data.contentfulContentPage?.blocks?.map((block) => ({
          type: block.__typename,
          contentfulId: block.contentful_id,
        })),
        currentLocale: LanguageHelper.getCurrentLocale(),
      }}
      staticConfigData={{
        eCommerce: {
          shopServicePaymentSlug: props.data.contentfulEcommerceConfig.shopServicePaymentPage.slug,
          shopServiceConfirmationSlug: props.data.contentfulEcommerceConfig.shopServiceConfirmationPage.slug,
          shopServiceCheckoutSlug: props.data.contentfulEcommerceConfig.shopServiceCheckoutPage.slug,
        },
      }}
    >
      <Page {...props} />
    </PageProviders>
  );
}

export const pageQuery = graphql`
  query ContentPageBy($slug: String!, $node_locale: String!, $contentful_id: String!) {
    contentfulContentPage(slug: { eq: $slug }) {
      id
      contentful_id
      node_locale
      pageTheme
      title
      slug
      description
      keywords
      socialMediaSharedImage {
        file {
          url
        }
      }
      navbar {
        ...Navbar
      }
      pageHeader {
        ...Header
      }
      metaTitle
      lockType
      lockValue
      lockInputLabel
      lockInputPlaceholder
      lockErrorMessage
      lockNecessaryProducts {
        ...ProductBlock
      }
      lockBlockingProducts {
        ...ProductBlock
      }
      lockMyPageMessageNotNecessaryProducts {
        ...TextBlock
      }
      lockMyPageMessageBlockingProducts {
        ...TextBlock
      }
      lockMyPageMessageNotLoggedIn {
        ...TextBlock
      }
      hideBroadcastMessage
      excludePageFromSiteBuild
      excludePageFromInternalSearch
      excludePageFromExternalSearch
      enableGoogleOptimizeAntiFlickerScript
      blocks {
        __typename
        ...TextBlock
        ...CardListItemBlock
        ...FactBlock
        ...ListBlock
        ...ShowBlock
        ...RestaurantBlock
        ...RideBlock
        ...GeneralContentBlock
        ...MediaGalleryBlock
        ...HeroBlock
        ...AdmissionBlock
        ...UpcomingBlock
        ...DailyScheduleBlock
        ...ProductListBlock
        ...ProductBlock
        ...AccommodationBlock
        ...BackgroundWrapperBlock
        ...RestaurantMenuBlock
        ...FormBlock
        ...InformationBlock
        ...Link
        ...IframeBlock
        ...GroupAdmissionBlock
        ...MyPageBlock
        ...SeparateJetpassBookingBlock
        ...SeasonPassPurchaseBlock
        ...FirstCampBlock
        ...CountdownBlock
      }
    }
    allContentfulContentPage(filter: { contentful_id: { eq: $contentful_id } }) {
      nodes {
        node_locale
        slug
      }
    }
    contentfulEcommerceConfig(node_locale: { eq: $node_locale }, primary: { eq: true }) {
      ...ECommerceConfig
    }
    contentfulSiteConfiguration(node_locale: { eq: $node_locale }, primary: { eq: true }) {
      ...SiteConfiguration
    }
    allLocalSearchSv {
      ...LunrSearchNodesSv
    }
    allLocalSearchEn {
      ...LunrSearchNodesEn
    }
  }
`;
